
import { defineComponent, ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { resolveApiUrl } from '@/services/api'
import {
  getTaskOutcomeAnalytics,
  getPageReworkAnalytics,
  listAdminTasks,
  TaskOutcomeAnalytics,
  PageReworkAnalytics,
  AdminTask,
  AdminTaskFilters,
  DateRangeFilter,
} from '@/services/admin'
function formatDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
function daysBefore(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return formatDate(date)
}
function percent(value: number): string {
  return `${Math.round(value * 100)}%`
}
export default defineComponent({
  name: 'TaskManagement',
  setup() {
    const presets = [
      { label: '近 7 天', days: 7 },
      { label: '近 30 天', days: 30 },
      { label: '近 90 天', days: 90 },
    ]
    const selectedPreset = ref(30)
    const startDate = ref(daysBefore(29))
    const endDate = ref(formatDate(new Date()))
    const outcome = ref<TaskOutcomeAnalytics | null>(null)
    const outcomeLoading = ref(false)
    const rework = ref<PageReworkAnalytics | null>(null)
    const reworkLoading = ref(false)
    const tasks = ref<AdminTask[]>([])
    const page = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const padDatePart = (value: number) => String(value).padStart(2, '0')
    // 任务列表筛选条件
    const statusOptions = ['queued', 'running', 'completed', 'failed', 'cancelled', 'expired']
    const filterStartDate = ref('')
    const filterEndDate = ref('')
    const filterTitle = ref('')
    const filterCreator = ref('')
    const filterStatus = ref('')
    const filterMode = ref('')
    const appliedFilters = ref<AdminTaskFilters>({})
    const buildFilters = (): AdminTaskFilters => {
      const f: AdminTaskFilters = {}
      if (filterStartDate.value) f.startDate = filterStartDate.value
      if (filterEndDate.value) f.endDate = filterEndDate.value
      if (filterTitle.value.trim()) f.title = filterTitle.value.trim()
      if (filterCreator.value.trim()) f.creator = filterCreator.value.trim()
      if (filterStatus.value) f.status = filterStatus.value
      if (filterMode.value.trim()) f.mode = filterMode.value.trim()
      return f
    }
    const applyTaskFilter = () => {
      appliedFilters.value = buildFilters()
      page.value = 1
      loadTasks()
    }
    const resetTaskFilter = () => {
      filterStartDate.value = ''
      filterEndDate.value = ''
      filterTitle.value = ''
      filterCreator.value = ''
      filterStatus.value = ''
      filterMode.value = ''
      appliedFilters.value = {}
      page.value = 1
      loadTasks()
    }
    const formatDateTime = (value?: string | null) => {
      if (!value) return '-'
      const date = new Date(value.replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return value
      const year = date.getFullYear()
      const month = padDatePart(date.getMonth() + 1)
      const day = padDatePart(date.getDate())
      const hour = padDatePart(date.getHours())
      const minute = padDatePart(date.getMinutes())
      const second = padDatePart(date.getSeconds())
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    }
    const creatorLabel = (task: AdminTask) => {
      if (task.creator_nickname && task.creator_email) {
        return `${task.creator_nickname} (${task.creator_email})`
      }
      return task.creator_nickname || task.creator_email || task.user_id
    }
    const diagnosticButtons = (task: AdminTask) => {
      const items = [
        { label: '报告', url: task.quality_report_url || '' },
        { label: '预览', url: task.preview_url || '' },
        { label: '对比', url: task.contact_sheet_url || '' },
        { label: '校验', url: task.validation_url || '' },
        { label: '耗时', url: task.performance_trace_url || '' },
        { label: '模型', url: task.agent_timings_url || '' },
      ]
      return items.filter(item => item.url)
    }
    // codex 会话产物：小文本内联查看，大文件（stdout/stderr）直接下载。
    const LOG_LABELS: Record<string, string> = { codex_metrics: '指标', codex_log_summary: '会话', codex_last_message: '回复', codex_stdout: 'stdout', codex_stderr: 'stderr' }
    const logButtons = (task: AdminTask) =>
      (task.diagnostic_artifacts || [])
        .filter(item => LOG_LABELS[item.kind] && item.storage_path)
        .map(item => ({ label: LOG_LABELS[item.kind], kind: item.kind, path: item.storage_path as string }))
    const logViewer = ref<{ title: string; content: string } | null>(null)
    const openLog = async (task: AdminTask, log: { label: string; kind: string; path: string }) => {
      const artifact = (task.diagnostic_artifacts || []).find(item => item.kind === log.kind)
      if ((log.kind === 'codex_stdout' || log.kind === 'codex_stderr') && artifact?.url) {
        downloadDiagnostic(task, { label: log.label, url: artifact.url })
        return
      }
      try {
        const token = localStorage.getItem('ppt_token')
        const res = await fetch(
          resolveApiUrl(`/api/admin-api/tasks/${task.id}/artifacts/content?path=${encodeURIComponent(log.path)}`),
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        logViewer.value = { title: `${task.title || task.id} · ${log.label}`, content: await res.text() }
      }
      catch {
        message.error(`读取${log.label}失败`)
      }
    }
    const formatK = (v?: number) => (!v ? '0' : v >= 1000 ? `${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : String(v))
    const metricsSummary = (task: AdminTask) => {
      const m = task.session_metrics
      if (!m) return ''
      const parts: string[] = []
      if (m.agent_model) parts.push(`Agent ${m.agent_model}`)
      const u = m.token_usage
      if (u) parts.push(`token 输入 ${formatK(u.input_tokens)}(缓存${formatK(u.cached_input_tokens)})/输出 ${formatK(u.output_tokens)}`)
      const imageCalls = m.image_calls || (m.runs || []).reduce((sum, run) => sum + (run.image_calls || 0), 0)
      const models = [...new Set((m.runs || []).map(run => run.image_model).filter(Boolean))]
      if (models.length) parts.push(`图像 ${models.join(',')}${imageCalls ? ` × ${imageCalls} 次` : ''}`)
      if (m.total_asset_files) parts.push(`资产 ${m.total_asset_files} 项(生成 ${m.generated_images || 0})`)
      return parts.join(' · ')
    }
    const qualityFailureText = (task: AdminTask) => {
      const summary = task.quality_failure_summary || []
      if (!summary.length) return ''
      return summary.map(item => `P${item.page_index + 1}: ${item.reason}`).join('；')
    }
    const downloadDiagnostic = async (task: AdminTask, artifact: { label: string; url: string }) => {
      if (!artifact.url) return
      try {
        const token = localStorage.getItem('ppt_token')
        const res = await fetch(resolveApiUrl(artifact.url), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = artifact.url.split('/').pop() || `${task.id}-${artifact.label}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(objectUrl)
      }
      catch {
        message.error(`下载${artifact.label}失败`)
      }
    }
    const downloadTaskPptx = async (task: AdminTask) => {
      if (!task.pptx_url) return
      try {
        const token = localStorage.getItem('ppt_token')
        const res = await fetch(resolveApiUrl(task.pptx_url), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = task.pptx_url.split('/').pop() || `${task.id}.pptx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(objectUrl)
      }
      catch {
        message.error('下载 PPTX 失败')
      }
    }
    const dateRange = computed<DateRangeFilter>(() => ({
      startDate: startDate.value,
      endDate: endDate.value,
    }))
    const rangeHint = computed(() => `${startDate.value} 至 ${endDate.value}`)
    const taskMetrics = computed(() => {
      const overall = outcome.value?.overall
      const pageStats = rework.value
      return [
        { label: '总任务数', value: overall?.total || 0 },
        { label: '成功任务', value: `${overall?.completed || 0} (${percent(overall?.successRate || 0)})` },
        { label: '失败任务', value: `${overall?.failed || 0} (${percent(overall?.failRate || 0)})` },
        { label: '取消任务', value: `${overall?.cancelled || 0} (${percent(overall?.cancelRate || 0)})` },
        { label: '返工页面', value: `${pageStats?.reworkedPages || 0} (${percent(pageStats?.reworkRate || 0)})` },
        { label: '含返工任务', value: `${pageStats?.tasksWithRework || 0} (${percent(pageStats?.taskReworkRate || 0)})` },
      ]
    })
    const validateRange = () => {
      if (startDate.value && endDate.value && startDate.value > endDate.value) {
        message.error('开始日期不能晚于结束日期')
        return false
      }
      return true
    }
    const loadOutcome = async () => {
      outcomeLoading.value = true
      try {
        outcome.value = await getTaskOutcomeAnalytics(dateRange.value)
      }
      catch {
        message.error('加载任务结果失败')
      }
      finally {
        outcomeLoading.value = false
      }
    }
    const loadRework = async () => {
      reworkLoading.value = true
      try {
        rework.value = await getPageReworkAnalytics(dateRange.value)
      }
      catch {
        message.error('加载返工数据失败')
      }
      finally {
        reworkLoading.value = false
      }
    }
    const loadMetrics = () => {
      if (!validateRange()) return
      loadOutcome()
      loadRework()
    }
    const applyPreset = (days: number) => {
      selectedPreset.value = days
      endDate.value = formatDate(new Date())
      startDate.value = daysBefore(days - 1)
      loadMetrics()
    }
    const loadTasks = async () => {
      try {
        const result = await listAdminTasks(page.value, pageSize.value, appliedFilters.value)
        tasks.value = result.list
        total.value = result.total
      }
      catch {
        message.error('加载任务列表失败')
      }
    }
    onMounted(() => {
      loadMetrics()
      loadTasks()
    })
    return {
      presets,
      selectedPreset,
      startDate,
      endDate,
      rangeHint,
      outcome,
      outcomeLoading,
      reworkLoading,
      taskMetrics,
      tasks,
      page,
      pageSize,
      total,
      loadTasks,
      loadMetrics,
      applyPreset,
      formatDateTime,
      creatorLabel,
      downloadTaskPptx,
      diagnosticButtons,
      qualityFailureText,
      downloadDiagnostic,
      statusOptions,
      filterStartDate,
      filterEndDate,
      filterTitle,
      filterCreator,
      filterStatus,
      filterMode,
      applyTaskFilter,
      resetTaskFilter,
      logButtons,
      openLog,
      logViewer,
      metricsSummary,
    }
  },
})
